import React, { useState, useMemo } from 'react';
import { FloatingPanel } from '../ui/Panel';
import {
  LUMBER_SPECIES,
  BEAM_SIZES,
  BEAM_LOAD_TYPES,
  ENGINEERED_BEAMS,
  GRADE_OPTIONS,
  PLY_OPTIONS,
} from '../../constants/structural';

const BeamHeaderCalculator = ({ onClose, isMobile = false }) => {
  const [spanFeet, setSpanFeet] = useState(10);
  const [spanInches, setSpanInches] = useState(0);
  const [tributaryFeet, setTributaryFeet] = useState(6);
  const [loadType, setLoadType] = useState('floor_beam');
  const [plies, setPlies] = useState(2);
  const [species, setSpecies] = useState('spf');
  const [grade, setGrade] = useState('#2');
  const [showEngineered, setShowEngineered] = useState(false);

  const spanTotalInches = spanFeet * 12 + spanInches;
  const spanTotalFeet = spanTotalInches / 12;

  // Calculate beam requirements and check each size
  const results = useMemo(() => {
    const load = BEAM_LOAD_TYPES[loadType];
    if (!load) return [];

    const speciesData = LUMBER_SPECIES[species];
    if (!speciesData) return [];

    const Fb = speciesData.Fb[grade] || 875;
    const E = speciesData.E || 1400000;

    // Total uniform load (plf = pounds per lineal foot)
    const totalPSF = load.live + load.dead;
    const w_plf = totalPSF * tributaryFeet;

    // Moment: M = wL²/8 (in-lbs)
    const L = spanTotalInches;
    const w_pli = w_plf / 12; // pounds per lineal inch
    const M = (w_pli * L * L) / 8;

    // Required section modulus for bending: S_req = M / Fb
    const S_req = M / Fb;

    // Required moment of inertia for deflection: delta = 5wL⁴/(384EI) ≤ L/deflLimit
    const deflLimit = load.deflectionLimit || 360;
    const I_req = (5 * w_pli * Math.pow(L, 4)) / (384 * E * (L / deflLimit));

    const items = [];
    for (const [sizeKey, sizeData] of Object.entries(BEAM_SIZES)) {
      // Effective properties with plies
      const effectiveS = sizeData.S * plies;
      const effectiveI = sizeData.I * plies;

      const bendingUtil = S_req / effectiveS;
      const deflectionUtil = I_req / effectiveI;
      const utilization = Math.max(bendingUtil, deflectionUtil);
      const governedBy = bendingUtil >= deflectionUtil ? 'bending' : 'deflection';
      const passes = utilization <= 1.0;
      const margin = ((1 / utilization) - 1) * 100;

      // Build display label with ply info
      const plyLabel = plies > 1 ? `(${plies}) ${sizeData.label}` : sizeData.label;

      items.push({
        key: sizeKey,
        label: plyLabel,
        passes,
        utilization,
        margin,
        governedBy,
        effectiveS,
        effectiveI,
      });
    }

    return items;
  }, [spanFeet, spanInches, tributaryFeet, loadType, plies, species, grade]);

  // Find recommended size
  const recommended = useMemo(() => {
    return results.find(r => r.passes && r.margin >= 10) || results.find(r => r.passes);
  }, [results]);

  // Engineered beam results
  const engineeredResults = useMemo(() => {
    if (!showEngineered) return [];
    const load = BEAM_LOAD_TYPES[loadType];
    if (!load) return [];

    const totalPSF = load.live + load.dead;
    const w_plf = totalPSF * tributaryFeet;
    const L = spanTotalInches;
    const w_pli = w_plf / 12;
    const M = (w_pli * L * L) / 8;
    const deflLimit = load.deflectionLimit || 360;

    const items = [];
    for (const [typeKey, typeData] of Object.entries(ENGINEERED_BEAMS)) {
      const Fb = typeData.Fb;
      const E = typeData.E;
      const S_req = M / Fb;
      const I_req = (5 * w_pli * Math.pow(L, 4)) / (384 * E * (L / deflLimit));

      for (const [sizeKey, sizeData] of Object.entries(typeData.sizes)) {
        // Engineered beams can also be doubled
        const effectiveS = sizeData.S * plies;
        const effectiveI = sizeData.I * plies;

        const bendingUtil = S_req / effectiveS;
        const deflectionUtil = I_req / effectiveI;
        const utilization = Math.max(bendingUtil, deflectionUtil);
        const governedBy = bendingUtil >= deflectionUtil ? 'bending' : 'deflection';
        const passes = utilization <= 1.0;
        const margin = ((1 / utilization) - 1) * 100;

        const plyLabel = plies > 1 ? `(${plies}) ${sizeData.label}` : sizeData.label;

        items.push({
          key: `${typeKey}-${sizeKey}`,
          type: typeData.label,
          label: plyLabel,
          passes,
          utilization,
          margin,
          governedBy,
        });
      }
    }

    return items;
  }, [showEngineered, spanTotalInches, tributaryFeet, loadType, plies]);

  const formatSpan = (inches) => {
    const ft = Math.floor(inches / 12);
    const inch = inches % 12;
    return inch > 0 ? `${ft}'-${inch}"` : `${ft}'-0"`;
  };

  const getUtilColor = (utilization) => {
    if (utilization <= 0.7) return '#4caf50';
    if (utilization <= 0.85) return '#8bc34a';
    if (utilization <= 0.95) return '#ff9800';
    if (utilization <= 1.0) return '#ff5722';
    return '#f44336';
  };

  const inputStyle = {
    padding: '6px 8px',
    background: '#1a1f24',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '12px',
    outline: 'none',
    width: '100%',
  };

  const labelStyle = {
    color: '#8090a0',
    fontSize: '10px',
    marginBottom: '3px',
    display: 'block',
  };

  const sectionTitle = {
    color: '#6080a0',
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    paddingBottom: '4px',
    marginBottom: '8px',
    marginTop: '12px',
  };

  return (
    <FloatingPanel
      title="Beam & Header Calculator"
      onClose={onClose}
      x={isMobile ? 10 : 380}
      y={isMobile ? 60 : 100}
      width={320}
      maxHeight={620}
      isMobile={isMobile}
      style={{ borderColor: 'rgba(76,175,80,0.3)' }}
    >
      <div style={{ padding: isMobile ? '8px' : '12px' }}>
        {/* Span Input */}
        <div style={sectionTitle}>Span</div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <div style={{ flex: 1 }}>
            <span style={labelStyle}>Feet</span>
            <input
              type="number"
              min={1}
              max={40}
              value={spanFeet}
              onChange={(e) => setSpanFeet(Math.max(1, Math.min(40, parseInt(e.target.value) || 0)))}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <span style={labelStyle}>Inches</span>
            <input
              type="number"
              min={0}
              max={11}
              value={spanInches}
              onChange={(e) => setSpanInches(Math.max(0, Math.min(11, parseInt(e.target.value) || 0)))}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <span style={labelStyle}>Trib. Width (ft)</span>
            <input
              type="number"
              min={1}
              max={20}
              value={tributaryFeet}
              onChange={(e) => setTributaryFeet(Math.max(1, Math.min(20, parseInt(e.target.value) || 0)))}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Load Type */}
        <div style={{ marginBottom: '8px' }}>
          <span style={labelStyle}>Load Type</span>
          <select
            value={loadType}
            onChange={(e) => setLoadType(e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            {Object.entries(BEAM_LOAD_TYPES).map(([key, lt]) => (
              <option key={key} value={key}>
                {lt.label} ({lt.live + lt.dead} psf)
              </option>
            ))}
          </select>
        </div>

        {/* Plies */}
        <div style={{ marginBottom: '8px' }}>
          <span style={labelStyle}>Plies</span>
          <div style={{ display: 'flex', gap: '4px' }}>
            {PLY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setPlies(opt.value)}
                style={{
                  flex: 1,
                  padding: '6px',
                  background: plies === opt.value ? 'rgba(76,175,80,0.2)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${plies === opt.value ? 'rgba(76,175,80,0.4)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '4px',
                  color: plies === opt.value ? '#4caf50' : '#8090a0',
                  fontSize: '11px',
                  fontWeight: plies === opt.value ? '600' : '400',
                  cursor: 'pointer',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Species & Grade */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <div style={{ flex: 2 }}>
            <span style={labelStyle}>Species</span>
            <select
              value={species}
              onChange={(e) => setSpecies(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              {Object.entries(LUMBER_SPECIES).map(([key, sp]) => (
                <option key={key} value={key}>{sp.label}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <span style={labelStyle}>Grade</span>
            <select
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              {GRADE_OPTIONS.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Engineered Toggle */}
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          color: '#8090a0',
          fontSize: '11px',
          cursor: 'pointer',
          marginBottom: '4px',
        }}>
          <input
            type="checkbox"
            checked={showEngineered}
            onChange={(e) => setShowEngineered(e.target.checked)}
            style={{ accentColor: '#4caf50' }}
          />
          Show LVL / PSL options
        </label>

        {/* Results */}
        <div style={sectionTitle}>
          Results — {formatSpan(spanTotalInches)} span, {tributaryFeet}' tributary
        </div>

        {/* Load summary */}
        <div style={{
          marginBottom: '8px',
          padding: '4px 8px',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '4px',
          fontSize: '10px',
          color: '#667',
        }}>
          Total load: {(BEAM_LOAD_TYPES[loadType]?.live + BEAM_LOAD_TYPES[loadType]?.dead) * tributaryFeet} plf
          ({BEAM_LOAD_TYPES[loadType]?.live + BEAM_LOAD_TYPES[loadType]?.dead} psf × {tributaryFeet}' trib)
        </div>

        {results.length === 0 ? (
          <div style={{ color: '#666', fontSize: '11px', padding: '8px 0' }}>
            No data available for this combination.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {results.map((r) => {
              const isRec = recommended && recommended.key === r.key;
              return (
                <div
                  key={r.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 8px',
                    background: isRec
                      ? 'rgba(76,175,80,0.15)'
                      : r.passes
                        ? 'rgba(255,255,255,0.03)'
                        : 'rgba(244,67,54,0.08)',
                    border: `1px solid ${isRec ? 'rgba(76,175,80,0.4)' : r.passes ? 'rgba(255,255,255,0.06)' : 'rgba(244,67,54,0.2)'}`,
                    borderRadius: '4px',
                  }}
                >
                  <span style={{
                    color: r.passes ? '#fff' : '#666',
                    fontWeight: isRec ? '700' : '500',
                    fontSize: '11px',
                    minWidth: '65px',
                  }}>
                    {r.label}
                  </span>

                  <div style={{
                    flex: 1,
                    height: '8px',
                    background: 'rgba(255,255,255,0.06)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${Math.min(100, r.utilization * 100)}%`,
                      height: '100%',
                      background: getUtilColor(r.utilization),
                      borderRadius: '4px',
                      transition: 'width 0.3s ease',
                    }} />
                  </div>

                  <span style={{
                    color: r.passes ? '#777' : '#555',
                    fontSize: '9px',
                    minWidth: '30px',
                    textAlign: 'right',
                  }}>
                    {r.governedBy === 'deflection' ? 'defl' : 'bend'}
                  </span>

                  <span style={{ fontSize: '11px', minWidth: '16px', textAlign: 'center' }}>
                    {r.passes ? (isRec ? '★' : '✓') : '✗'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Recommended callout */}
        {recommended && (
          <div style={{
            marginTop: '8px',
            padding: '8px',
            background: 'rgba(76,175,80,0.1)',
            border: '1px solid rgba(76,175,80,0.3)',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#8bc34a',
          }}>
            <strong>Recommended: {recommended.label}</strong>
            <span style={{ color: '#6a8a6a', marginLeft: '8px' }}>
              {Math.round(recommended.margin)}% margin, governed by {recommended.governedBy}
            </span>
          </div>
        )}

        {/* Engineered Results */}
        {showEngineered && engineeredResults.length > 0 && (
          <>
            <div style={sectionTitle}>Engineered Options</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {engineeredResults.map((r) => (
                <div
                  key={r.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 8px',
                    background: r.passes ? 'rgba(255,255,255,0.03)' : 'rgba(244,67,54,0.08)',
                    border: `1px solid ${r.passes ? 'rgba(255,255,255,0.06)' : 'rgba(244,67,54,0.2)'}`,
                    borderRadius: '4px',
                  }}
                >
                  <span style={{
                    color: r.passes ? '#fff' : '#666',
                    fontWeight: '500',
                    fontSize: '10px',
                    minWidth: '90px',
                  }}>
                    {r.label}
                  </span>

                  <div style={{
                    flex: 1,
                    height: '8px',
                    background: 'rgba(255,255,255,0.06)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${Math.min(100, r.utilization * 100)}%`,
                      height: '100%',
                      background: getUtilColor(r.utilization),
                      borderRadius: '4px',
                      transition: 'width 0.3s ease',
                    }} />
                  </div>

                  <span style={{
                    color: r.passes ? '#777' : '#555',
                    fontSize: '9px',
                    minWidth: '30px',
                    textAlign: 'right',
                  }}>
                    {r.governedBy === 'deflection' ? 'defl' : 'bend'}
                  </span>

                  <span style={{ fontSize: '11px', minWidth: '16px', textAlign: 'center' }}>
                    {r.passes ? '✓' : '✗'}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Footer info */}
        <div style={{
          marginTop: '12px',
          padding: '6px 8px',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '4px',
          fontSize: '10px',
          color: '#556',
        }}>
          {BEAM_LOAD_TYPES[loadType]?.description}
          <br />
          Live: {BEAM_LOAD_TYPES[loadType]?.live} psf + Dead: {BEAM_LOAD_TYPES[loadType]?.dead} psf
          <br />
          Deflection limit: L/{BEAM_LOAD_TYPES[loadType]?.deflectionLimit}
          <br />
          <span style={{ color: '#445', fontStyle: 'italic' }}>
            Simplified analysis. Verify with engineer for critical applications.
          </span>
        </div>
      </div>
    </FloatingPanel>
  );
};

export default BeamHeaderCalculator;
